// The Security page's Public keys section (consistency-pass spec §3): one
// KeySection card per key, newest first as the API orders them, plus the
// draft "New key" while one is being added.

import { Button, type SectionNavItem } from "@pej/kore/components";
import { Plus } from "lucide-react";
import type { ContextKey, SecuritySettings } from "@/api/types";
import { useSectionEditController } from "@/components/section-page/section-edit";
import { removeRefusal } from "./key-fields";
import { KeySection } from "./key-section";
import { SECURITY_SECTION } from "./security-sections";

export interface KeysSectionProps {
    workspaceId: string;
    settings: SecuritySettings;
    draft: boolean;
    onDraft: (open: boolean) => void;
}

export function KeysSection({ workspaceId, settings, draft, onDraft }: KeysSectionProps) {
    const controller = useSectionEditController();
    const addBlocked = controller.isAnySectionActive || draft;
    const refusal = removeRefusal(settings.keys, settings.requireSignedContext);
    const titleId = `${SECURITY_SECTION.keys}-title`;
    return (
        <section
            id={SECURITY_SECTION.keys}
            aria-labelledby={titleId}
            className="flex scroll-mt-[4.5rem] flex-col gap-6"
        >
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 id={titleId} className="text-lg font-semibold">
                        Public keys
                    </h2>
                    <p className="mt-1 text-sm text-kore-text-muted">
                        Signed context is verified against these. They are public: nothing here is secret, and the
                        private half never leaves the system that signs.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={addBlocked}
                    title={addBlocked ? "Save or cancel the current edit first." : undefined}
                    onClick={() => onDraft(true)}
                >
                    <Plus aria-hidden="true" />
                    Add key
                </Button>
            </div>
            {settings.keys.length === 0 && !draft && (
                <p className="text-sm text-kore-text-muted" data-testid="no-keys">
                    No keys yet. Context sent by an embedding system is accepted and recorded as unverified.
                </p>
            )}
            {settings.keys.map((key) => (
                <KeySection key={key.id} workspaceId={workspaceId} contextKey={key} removeRefusal={refusal} />
            ))}
            {draft && <KeySection workspaceId={workspaceId} contextKey={null} onDraftDone={() => onDraft(false)} />}
        </section>
    );
}

export function keyNavItems(
    keys: readonly ContextKey[],
    draft: boolean,
    errors: Record<string, boolean>,
): SectionNavItem[] {
    const children: SectionNavItem[] = keys.map((key) => ({
        id: SECURITY_SECTION.key(key.id),
        label: key.label,
        hasError: errors[SECURITY_SECTION.key(key.id)],
    }));
    if (draft)
        children.push({ id: SECURITY_SECTION.newKey, label: "New key", hasError: errors[SECURITY_SECTION.newKey] });
    return [{ id: SECURITY_SECTION.keys, label: "Public keys", children }];
}
